import React, { createContext, useContext, useState, useEffect } from 'react';

// Theme definitions
export const themes = {
  dark: {
    name: 'dark',
    colors: {
      // Background colors
      background: '#000000',
      surface: 'rgba(26, 26, 46, 0.95)',
      surfaceSecondary: 'rgba(22, 33, 62, 0.95)',
      
      // Text colors
      text: '#ffffff',
      textSecondary: '#888888',
      textMuted: '#666666',
      
      // UI colors
      border: 'rgba(255, 255, 255, 0.3)',
      borderLight: 'rgba(255, 255, 255, 0.1)',
      shadow: 'rgba(0, 0, 0, 0.3)',
      
      // Filter component specific
      filterBorder: 'rgba(255, 255, 255, 0.1)',
      
      // Bubble transparency
      bubbleAlpha: '0.3',
      
      // Interactive colors
      hover: 'linear-gradient(135deg, rgba(100, 255, 218, 0.9) 0%, rgba(187, 134, 252, 0.9) 100%)',
      hoverText: '#000000',
      
      // Scrollbar
      scrollbarTrack: '#000000',
      scrollbarThumb: '#c0c0c0',
      scrollbarThumbHover: '#ffffff',
      
      // Selection
      selection: 'rgba(192, 192, 192, 0.3)',
      
      // Timeline
      timelineAxis: 'linear-gradient(180deg, rgba(255, 255, 255, 0.8) 0%, rgba(192, 192, 192, 0.4) 100%)',
      
      // Active state gradients
      activeGradient: 'linear-gradient(135deg, rgba(100, 255, 218, 0.2) 0%, rgba(187, 134, 252, 0.2) 100%)',
      activeGradientHover: 'linear-gradient(135deg, rgba(100, 255, 218, 0.3) 0%, rgba(187, 134, 252, 0.3) 100%)',
    }
  },
  light: {
    name: 'light',
    colors: {
      // Background colors
      background: '#ffffff',
      surface: 'rgba(255, 255, 255, 0.95)',
      surfaceSecondary: 'rgba(248, 249, 250, 0.95)',
      
      // Text colors
      text: '#1a1a1a',
      textSecondary: '#666666',
      textMuted: '#999999',
      
      // UI colors
      border: 'rgba(0, 0, 0, 0.15)',
      borderLight: 'rgba(0, 0, 0, 0.08)',
      shadow: 'rgba(0, 0, 0, 0.1)',
      
      // Filter component specific
      filterBorder: 'rgba(0, 0, 0, 0.25)',
      
      // Bubble transparency
      bubbleAlpha: '0.5',
      
      // Interactive colors
      hover: 'linear-gradient(135deg, rgba(59, 130, 246, 0.9) 0%, rgba(147, 51, 234, 0.9) 100%)',
      hoverText: '#ffffff',
      
      // Scrollbar
      scrollbarTrack: '#f1f1f1',
      scrollbarThumb: '#888888',
      scrollbarThumbHover: '#555555',
      
      // Selection
      selection: 'rgba(59, 130, 246, 0.3)',
      
      // Timeline
      timelineAxis: 'linear-gradient(180deg, rgba(0, 0, 0, 0.6) 0%, rgba(100, 100, 100, 0.3) 100%)',
      
      // Active state gradients
      activeGradient: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(147, 51, 234, 0.2) 100%)',
      activeGradientHover: 'linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(147, 51, 234, 0.3) 100%)',
    }
  }
};

// Create the context
const ThemeContext = createContext();

// Theme provider component
export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    // Check localStorage for saved theme preference
    const savedTheme = localStorage.getItem('theme');
    return savedTheme || 'dark';
  });

  // Update localStorage when theme changes
  useEffect(() => {
    localStorage.setItem('theme', theme);
    
    // Update CSS custom properties for global styles
    const root = document.documentElement;
    const currentTheme = themes[theme];
    
    Object.entries(currentTheme.colors).forEach(([key, value]) => {
      root.style.setProperty(`--color-${key}`, value);
    });
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prevTheme => prevTheme === 'dark' ? 'light' : 'dark');
  };

  const value = {
    theme,
    themeData: themes[theme],
    toggleTheme,
    isDark: theme === 'dark'
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

// Custom hook to use theme
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};